import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Calendar, Mail, Phone, MapPin, Users, Heart } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { addLocalBooking, seedLocalDefaultsIfEmpty } from '../lib/localContentStore';
import { createBooking } from '../lib/firebaseData';

const BookingModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    location: '',
    eventType: 'wedding',
    guestCount: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    seedLocalDefaultsIfEmpty();

    try {
      await createBooking({
        ...formData,
        guestCount: formData.guestCount ? parseInt(formData.guestCount, 10) : null
      });

      toast({
        title: "Booking Request Received!",
        description: "Thank you for your interest. We'll contact you within 24 hours.",
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventDate: '',
        location: '',
        eventType: 'wedding',
        guestCount: '',
        message: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Booking submission error:', error);
      const localBooking = {
        id: `local-booking-${Date.now()}`,
        ...formData,
        guestCount: formData.guestCount ? parseInt(formData.guestCount, 10) : null,
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      addLocalBooking(localBooking);
      toast({
        title: "Saved Locally",
        description: "Booking write was blocked by the live database. Your request is saved in this browser and will appear in admin on this device.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventDate: '',
        location: '',
        eventType: 'wedding',
        guestCount: '',
        message: ''
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-500" />
            Book Your Event
          </DialogTitle>
          <DialogDescription>
            Fill out the form below and we'll get back to you within 24 hours to discuss your special day.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John & Jane Doe"
              required
              className="border-gray-300"
            />
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                <Mail className="w-4 h-4 inline mr-1" />
                Email *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                className="border-gray-300"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone *
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                required
                className="border-gray-300"
              />
            </div>
          </div>

          {/* Event Date & Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="eventDate">
                <Calendar className="w-4 h-4 inline mr-1" />
                Event Date *
              </Label>
              <Input
                id="eventDate"
                name="eventDate"
                type="date"
                value={formData.eventDate}
                onChange={handleChange}
                required
                className="border-gray-300"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">
                <MapPin className="w-4 h-4 inline mr-1" />
                Location *
              </Label>
              <Input
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="City, State"
                required
                className="border-gray-300"
              />
            </div>
          </div>

          {/* Guest Count */}
          <div className="space-y-2">
            <Label htmlFor="guestCount">
              <Users className="w-4 h-4 inline mr-1" />
              Expected Guest Count
            </Label>
            <Input
              id="guestCount"
              name="guestCount"
              type="number"
              value={formData.guestCount}
              onChange={handleChange}
              placeholder="150"
              className="border-gray-300"
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Tell us about your event</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Share any special details, requests, or questions about your event..."
              rows={4}
              className="border-gray-300 resize-none"
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-gray-900 hover:bg-gray-800 text-white py-6 text-base"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-8 py-6 text-base"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
